import { Fragment, h } from 'preact';
import { useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Navigate, Outlet, Route, Routes, UNSAFE_NavigationContext, useBlocker, useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import clipboardCopy from 'clipboard-copy';
import { Dropdown, ProgressBar } from 'react-bootstrap';
import { GlobalState } from 'src/js';
import { LisuClient, LisuClientLicenseList, LisuClientEditLinkList } from '../clients';
import { RemixIcon } from 'src/js/utils/RemixIcon';

export function LisuAccount(props: { state: GlobalState, clientId?: string }) {
    
    const urlParams = useParams();
    const clientId = props.clientId ?? urlParams.clientId ?? 'new';
    
    return <Fragment>
        
        <div className='row justify-content-center'>
            
            {/*
            <div className='col-12 col-md-6 col-lg-4 mb-3'>
                <div className='m-3 lisu-card'>
                    <h4 className='mb-2'><RemixIcon icon='ri-lock-password-line me-2'/>Päivitä käyttäjätietoja</h4>
                    <p style={{ lineHeight: '105%' }}>
                        <small>Tällä lomakkeella voit päivittää käyttäjä&shy;tietojasi.
                        Huomioit&shy;han, että sähkö&shy;postisi on myös käyttäjänimesi.</small>
                    </p>
                    <InfoUpdateForm/>
                </div>
            </div>
            */}
            
            <div className='col-12 col-md-6 col-lg-4'>
                <div className='m-3 lisu-card'>
                    <h4 className='mb-2'><RemixIcon icon='ri-lock-password-line me-2'/>Vaihda salasana</h4>
                    <p style={{ lineHeight: '105%' }}>
                        <small>Tällä lomakkeella voit päivittää salasanasi.</small>
                    </p>
                    <PasswordChangeForm/>
                </div>
            </div>
            
        </div>
        
    </Fragment>;
    
}

function InfoUpdateForm(props: {}) {
    
    const [values, setValues] = useState<any>({});
    const [submitError, setSubmitError] = useState<string | null>(null);
    
    return <form
        id='updateInfoForm'
        onSubmit={e => {
            
            e.preventDefault();
            
        }}
        >
        
        <div className='mb-3'>
            <label>Sähköposti</label>
            <input
                name='email'
                type='email'
                autoComplete='off'
                className='form-control'
                value={values.email ?? ''}
                onInput={e => setValues({ ...values, email: e.currentTarget.value })}
                />
        </div>
        
        {submitError && <div className='alert alert-danger'><RemixIcon icon='ri-error-warning-line'/> {submitError}</div>}
        
        <div className='text-center'>
            <button
                target='updateInfoForm'
                type='submit'
                className='btn btn-sm btn-primary'
                >
                Päivitä tiedot
            </button>
        </div>
        
    </form>;
    
}

function PasswordChangeForm(props: {}) {
    
    const [oldPassword, setOldPassword] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [password2, setPassword2] = useState<string>('');
    
    const [submitError, setSubmitError] = useState<string | null>(null);
    
    const passwordScore = useMemo<number>(() => calculatePasswordPoints(password), [password]);
    const passwordScoreColor = useMemo<string>(() => {
        
        const SCORES = [
            'danger',
            'danger',
            'warning',
            'warning',
            'warning',
            'success',
            'success'
        ];
        
        return SCORES[Math.floor(passwordScore*(SCORES.length-1))] ?? 'warning';
        
    }, [passwordScore]);
    
    return <form
        id='changePasswordForm'
        onSubmit={e => {
            
            e.preventDefault();
            
            if (password.trim() == '') {
                setSubmitError('Uusi salasana ei voi olla tyhjä.');
                return;
            }
            
            if (password.length < 8) {
                setSubmitError('Salasanan tulee olla vähintään 8 merkkiä pitkä.');
                return;
            }
            
            if (password != password2) {
                setSubmitError('Salasanat eivät täsmää.');
                return;
            }
            
            fetch('/api/changePassword', {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({
                    password,
                    password2,
                    oldPassword
                })
            })
                .then(x => x.json())
                .then(resp => {
                    
                    if (resp.error) {
                        setSubmitError(`${resp.error}`);
                        return;
                    }
                    
                    if (resp.success) {
                        alert('Salasanasi on nyt vaihdettu.');
                        window.location.reload();
                        return;
                    }
                    
                    throw new Error(`Unknown state`);
                    
                })
                .catch(err => {
                    console.error(err);
                    alert(`Virhe: ${err}`);
                })
            
        }}
        >
        
        <div className='mb-3'>
            <label>Nykyinen salasana</label>
            <input
                name='currentPassword'
                type='password'
                autoComplete='off'
                className='form-control'
                value={oldPassword}
                onInput={e => setOldPassword(e.currentTarget.value)}
                />
        </div>
        
        <div className='mb-3'>
            <label>Uusi salasana</label>
            <input
                name='password'
                type='password'
                autoComplete='off'
                className='form-control'
                value={password}
                onInput={e => setPassword(e.currentTarget.value)}
                />
        </div>
        
        <div className='mb-3 p-2 pb-3 border border-dark bg-light rounded' style={{
            display: password == '' ? 'none' : ''
        }}>
            <h6>Salasanan vahvuus</h6>
            <ProgressBar
                variant={passwordScoreColor}
                now={passwordScore*100}
                />
        </div>
        
        <div className='mb-3'>
            <label>Toista uusi salasana</label>
            <input
                name='password2'
                type='password'
                autoComplete='off'
                className={password == password2 ? 'form-control' : 'form-control is-invalid'}
                value={password2}
                onInput={e => setPassword2(e.currentTarget.value)}
                />
            <div className='invalid-feedback'>
                <RemixIcon icon='ri-error-warning-line'/> Salasanat eivät täsmää
            </div>
        </div>
        
        {submitError && <div className='alert alert-danger'><RemixIcon icon='ri-error-warning-line'/> {submitError}</div>}
        
        <div className='text-center'>
            <button
                target='changePasswordForm'
                type='submit'
                className='btn btn-sm btn-primary'
                >
                Vaihda salasana
            </button>
        </div>
        
    </form>;
    
}

function calculatePasswordPoints(pass: string) {
    
    let score = 0;
    
    if (pass.match(/[A-Z]/gm)) {
        score += 1;
    }
    
    if (pass.match(/[a-z]/gm)) {
        score += 1;
    }
    
    if (pass.match(/[0-9]/gm)) {
        score += 1;
    }
    
    if (pass.match(/[^A-Za-z0-9]/gm)) {
        score += 1;
    }
    
    if (pass.length >= 16) {
        score += 3;
    } else if (pass.length >= 12) {
        score += 2;
    } else if (pass.length >= 8) {
        score += 1;
    }
    
    return score/7;
    
}